import {
  Conversation,
  Message,
  MessageSender,
  ServerConversation,
  ServerMessage,
} from '../models/Conversation.model'

export const mapServerConversationToClientConversation = (
  serverConversation: ServerConversation,
  isPinned: boolean = false
): Conversation => {
  return {
    id: serverConversation.conversationId,
    title: serverConversation.title,
    createdAt: new Date(serverConversation.startTime).getTime(),
    messages: [],
    isAwaitingResponse: false,
    messagesNextToken: null,
    isPinned: isPinned,
  }
}

export const mapServerMessageToClientMessage = (serverMessage: ServerMessage): Message => {
  const { messageId, body, time, type } = serverMessage

  if (!messageId || !body || !time || !type) {
    throw new Error('Invalid message data from server: missing fields')
  }
  if (
    typeof messageId !== 'string' ||
    typeof body !== 'string' ||
    typeof time !== 'string' ||
    typeof type !== 'string'
  ) {
    throw new Error('Invalid message data types from server')
  }
  if (type !== 'USER' && type !== 'SYSTEM') {
    throw new Error('Invalid message type from server')
  }

  const createdAt = new Date(time).getTime()

  const clientMessage: Message = {
    id: messageId,
    from: type === 'USER' ? MessageSender.USER : MessageSender.BOT,
    text: body,
    createdAt,
  }

  if (serverMessage.type === 'USER') {
    clientMessage.attachments = (serverMessage.attachments ?? [])
      .filter(att => att.status === 'SUCCESS')
      .map(att => att.name.replace(/^\d+-/, ''))
  } else if (serverMessage.type === 'SYSTEM') {
    clientMessage.sources =
      serverMessage.sourceAttribution?.map((source) => ({
        citationNumber: source.citationNumber,
        snippet: source.snippet,
        title: source.title,
        url: source.url,
        textMessageSegments:
          source.textMessageSegments?.map((segment) => ({
            beginOffset: segment.beginOffset,
            endOffset: segment.endOffset,
            snippetExcerpt: { text: segment.snippetExcerpt.text },
          })) ?? [],
      })) ?? []
  }

  return clientMessage
}

// New helper function to map conversations from the new API response format
export const mapConversationsFromApiResponse = (
  conversations: ServerConversation[],
  pinnedConversations: ServerConversation[] = []
): Conversation[] => {
  // Map pinned conversations first
  const mappedPinnedConversations = pinnedConversations.map((serverConv) =>
    mapServerConversationToClientConversation(serverConv, true)
  )

  // Map regular conversations, excluding those that are already pinned
  const pinnedIds = new Set(pinnedConversations.map(pc => pc.conversationId))
  const mappedRegularConversations = conversations
    .filter(conv => !pinnedIds.has(conv.conversationId))
    .map((serverConv) => mapServerConversationToClientConversation(serverConv, false))

  // Return combined array with pinned conversations first
  return [...mappedPinnedConversations, ...mappedRegularConversations]
}
